import { useStore } from '@/state/store';
import { fmtPct } from '@/lib/format';
import { sofrFreshness } from '@/lib/dataFreshness';

/**
 * Data provenance stamps, fallback warnings, and the SOFR liveness check (§10.9).
 *
 * The liveness notice reports here rather than in CI on purpose. When the refresh pipeline
 * died in August 2026 it failed loudly every single day — in a place nobody reads — and the
 * data sat frozen for three weeks. The dashboard is where the number is actually looked at,
 * so it is where "this is old" has to appear.
 */
export function DataBadges() {
  const sofrRate = useStore((s) => s.sofrLatestRate);
  const sofrDate = useStore((s) => s.sofrLatestDate);
  const warnings = useStore((s) => s.dataWarnings);

  const freshness = sofrFreshness(sofrDate, new Date());
  // The inline date sits on the page surface, so it uses the theme-flipping `-surface`
  // role; the banner below sits on the fixed tint and uses the plain token.
  const dateTone =
    freshness.level === 'dead'
      ? 'text-danger-surface font-medium'
      : freshness.level === 'stale'
        ? 'text-caution font-medium'
        : '';

  return (
    <div className="flex flex-col gap-1 text-xs text-slate-500 dark:text-slate-400">
      <div className="flex flex-wrap gap-x-4 gap-y-1">
        <span>
          SOFR <strong className="text-slate-700 dark:text-slate-200">{fmtPct(sofrRate)}</strong>
          {sofrDate ? <span className={dateTone}> as of {sofrDate}</span> : ''}
        </span>
      </div>
      {freshness.message && (
        <div
          className={
            freshness.level === 'dead'
              ? 'rounded border border-danger/40 bg-danger-bg px-2 py-1 text-danger'
              : 'rounded bg-caution-bg px-2 py-1 text-caution'
          }
          role={freshness.level === 'dead' ? 'alert' : 'status'}
        >
          {freshness.message}
        </div>
      )}
      {warnings.length > 0 && (
        <div className="rounded bg-caution-bg px-2 py-1 text-caution" role="status">
          {warnings.join(' ')}
        </div>
      )}
    </div>
  );
}
